import React, {type ReactNode, useMemo} from 'react';
import {twMerge} from 'tailwind-merge';

import {SettingsContentWrapper} from '../components/SettingsContentWrapper';
import {SettingToggleRow} from '../components/SettingToggleRow';
import {
  useShowEmptySections,
  useSortAlphabetically,
  useTrackWorkspaceSettingChange,
  useUpdateLayoutSettingAndSortPanels,
  useUpdateWorkspaceLayoutSettings,
} from '../hooks';
import {getWorkspaceLayoutSettingWithDefaults} from './getWorkspaceLayoutSettings';
import {WorkspaceLayoutAutoOrganizePrefix} from './WorkspaceLayoutAutoOrganizePrefix';
import {WorkspaceMode} from './WorkspaceMode';

type WorkspaceLayoutSettingsProps = {
  contentHeader?: ReactNode;
};
export const WorkspaceLayoutSettings = ({
  contentHeader,
}: WorkspaceLayoutSettingsProps) => {
  const updateWorkspaceLayoutSettings = useUpdateWorkspaceLayoutSettings();
  const updateLayoutSettingAndSortPanels =
    useUpdateLayoutSettingAndSortPanels();
  const trackWorkspaceSettingChange = useTrackWorkspaceSettingChange();
  const showEmptySectionsUndefaulted = useShowEmptySections();
  const sortAlphabeticallyUndefaulted = useSortAlphabetically();
  const {showEmptySections, sortAlphabetically} = useMemo(
    () =>
      getWorkspaceLayoutSettingWithDefaults({
        showEmptySections: showEmptySectionsUndefaulted,
        sortAlphabetically: sortAlphabeticallyUndefaulted,
      }),
    [showEmptySectionsUndefaulted, sortAlphabeticallyUndefaulted]
  );

  return (
    <SettingsContentWrapper
      title="Workspace layout"
      description="Define the overall structure of this workspace, including sectioning logic and panel organization."
      header={contentHeader}>
      <div
        className={twMerge(
          'border-t-[1px] border-solid border-wb-border',
          'mt-12 gap-20'
        )}
      />
      <div
        data-test="workspace-layout-settings"
        className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
        <WorkspaceMode />
        <SettingToggleRow
          displayValue={!showEmptySections}
          label="Hide empty sections during search"
          onCheckedChange={(newValue: boolean) => {
            // Important: we need to flip the value to reflect label text
            updateWorkspaceLayoutSettings({showEmptySections: !newValue});
            trackWorkspaceSettingChange(
              'showEmptySections',
              newValue.toString()
            );
          }}
        />
        <SettingToggleRow
          displayValue={sortAlphabetically}
          label="Sort panels alphabetically"
          onCheckedChange={(newValue: boolean) => {
            updateLayoutSettingAndSortPanels(newValue);
          }}
        />
        <WorkspaceLayoutAutoOrganizePrefix />
      </div>
    </SettingsContentWrapper>
  );
};
